import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Transaction, PaymentMethod, ExpenseCategory, IncomeCategory, TransactionType, PointProvider } from '../types';
import { storageService } from '../services/storageService';
import { userService } from '../services/userService';
import { normalizeNumber } from '../utils/numberUtils';
import { CATEGORY_COLORS, PAYMENT_METHOD_LABELS, POINT_COLORS, POINT_PROVIDER_LABELS, PAYMENT_DETAILS } from '../constants';
import { Loader2, TrendingUp, CreditCard, ArrowUpRight, ArrowDownLeft, Wallet, QrCode, Save, X, Coins, Sparkles, Building2, Target, Pencil, Check, Store, EyeOff, Eye } from 'lucide-react';

interface TopPageProps {
  userId: string;
}

const METHOD_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#84cc16', '#06b6d4'];

const METHOD_CONFIG: Record<string, { label: string, icon: any, color: string, text: string, bg: string }> = {
  [PaymentMethod.QR_CODE]: { label: 'QR・コード決済', icon: QrCode, color: 'from-orange-400 to-pink-500', text: 'text-orange-600', bg: 'bg-orange-50' },
  [PaymentMethod.CASH]: { label: '現金', icon: Wallet, color: 'from-emerald-400 to-teal-500', text: 'text-emerald-600', bg: 'bg-emerald-50' },
  [PaymentMethod.E_MONEY]: { label: '電子マネー', icon: CreditCard, color: 'from-blue-400 to-indigo-500', text: 'text-blue-600', bg: 'bg-blue-50' },
  [PaymentMethod.PREPAID_CARD]: { label: 'プリペイドカード', icon: CreditCard, color: 'from-cyan-400 to-blue-500', text: 'text-cyan-600', bg: 'bg-cyan-50' },
  [PaymentMethod.CREDIT_CARD]: { label: 'クレジットカード', icon: CreditCard, color: 'from-indigo-400 to-purple-500', text: 'text-indigo-600', bg: 'bg-indigo-50' },
  [PaymentMethod.BANK_TRANSFER]: { label: '銀行口座', icon: Building2, color: 'from-slate-400 to-slate-500', text: 'text-slate-600', bg: 'bg-slate-50' },
  [PaymentMethod.DEBIT_CARD]: { label: 'デビットカード', icon: CreditCard, color: 'from-violet-400 to-fuchsia-500', text: 'text-violet-600', bg: 'bg-violet-50' },
};

const DEFAULT_TARGET_METHODS = [PaymentMethod.QR_CODE, PaymentMethod.CASH, PaymentMethod.E_MONEY];

type Tab = 'assets' | 'analysis';

// Animated Number Component with cleanup
const AnimatedNumber = ({ value }: { value: number }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let animationFrameId: number;
    let startTimestamp: number | null = null;
    const duration = 1200; // Animation duration in ms
    const start = 0; 
    const end = value;

    if (start === end) {
        setDisplayValue(end);
        return;
    }

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      
      // Ease Out Quart (1 - (1-x)^4)
      const ease = 1 - Math.pow(1 - progress, 4);
      
      setDisplayValue(start + (end - start) * ease);

      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(step);
      }
    };

    animationFrameId = window.requestAnimationFrame(step);

    return () => {
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [value]);

  return <>{Math.round(displayValue).toLocaleString()}</>;
};

export const TopPage: React.FC<TopPageProps> = ({ userId }) => {
  const [activeTab, setActiveTab] = useState<Tab>('assets');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [monthlyBudget, setMonthlyBudget] = useState(0);
  const [showBalance, setShowBalance] = useState(true);
  const [balanceSettings, setBalanceSettings] = useState<any>(null); // Use any for now or import BalanceSettings
  
  // Balance Edit State
  const [editingMethod, setEditingMethod] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  // Budget Edit State
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState('');
  const [isSavingBudget, setIsSavingBudget] = useState(false);

  useEffect(() => {
    loadData();
  }, [userId]);

  const loadData = async () => {
    setLoading(true);
    const [data, settings, userPrefs] = await Promise.all([
        storageService.getTransactions(userId),
        storageService.getSettings(userId),
        userService.getUserPreferences(userId)
    ]);
    setTransactions(data);
    setMonthlyBudget(settings.monthlyBudget);
    setBalanceSettings(settings.balanceSettings);
    
    if (userPrefs) {
        setShowBalance(userPrefs.displayTotalBalance);
    }
    
    setLoading(false);
  };

  // --- Balance Logic ---
  const calculateBalance = (methodKey: string) => {
    if (balanceSettings?.enabled) {
        // If it's a detail key, return its stored balance directly
        if (methodKey.includes(':')) {
             return balanceSettings.balances?.[methodKey] || 0;
        }

        // If it's a parent key, check if it has included children
        const method = methodKey as PaymentMethod;
        const includedChildren = (balanceSettings.includedMethods || []).filter(
            k => k.startsWith(`${method}:`)
        );

        if (includedChildren.length > 0) {
            // Has children: sum only children (parent key value is a cached rollup, skip to avoid double-count)
            return includedChildren.reduce((sum, childKey) => {
                return sum + (balanceSettings.balances?.[childKey] || 0);
            }, 0);
        }

        // No children: use parent key value directly
        return balanceSettings.balances?.[methodKey] || 0;
    }

    // Fallback to calculation from transactions (Legacy behavior)
    if (methodKey.includes(':')) {
        const [method, detail] = methodKey.split(':');
        return transactions.reduce((acc, t) => {
            if (t.method === method && t.paymentDetail === detail) {
                if (t.type === 'income') return acc + t.amount;
                if (t.type === 'expense') return acc - t.amount;
                if (t.type === 'charge') return acc + t.amount;
            }
            return acc;
        }, 0);
    }

    return transactions.reduce((acc, t) => {
        if (t.method === methodKey) {
            if (t.type === 'income') return acc + t.amount;
            if (t.type === 'expense') return acc - t.amount;
            if (t.type === 'charge') return acc + t.amount;
        }
        if (t.type === 'charge' && t.sourceMethod === methodKey) {
            return acc - t.amount;
        }
        return acc;
    }, 0);
  };

  const calculateTotalBalance = () => {
      const balances = balanceSettings?.balances || {};
      const includedMethods: string[] = balanceSettings?.includedMethods || [];

      // If balanceSettings is enabled, use includedMethods to determine what to sum.
      // Sum only leaf keys (keys with ':') + parent keys that have no children in includedMethods.
      // This prevents double-counting parent + child.
      if (balanceSettings?.enabled && includedMethods.length > 0) {
          const parentKeys = includedMethods.filter(k => !k.includes(':'));
          const childKeys = includedMethods.filter(k => k.includes(':'));

          let total = 0;
          parentKeys.forEach(parentKey => {
              // Check if this parent has any children in includedMethods
              const hasChildren = childKeys.some(ck => ck.startsWith(`${parentKey}:`));
              if (hasChildren) {
                  // Sum only children (parent key value is redundant / already rolled up)
                  childKeys
                      .filter(ck => ck.startsWith(`${parentKey}:`))
                      .forEach(ck => { total += Number(balances[ck] || 0); });
              } else {
                  total += Number(balances[parentKey] || 0);
              }
          });
          // Also add any child keys that have no corresponding parent in includedMethods
          childKeys.forEach(ck => {
              const parentKey = ck.split(':')[0];
              if (!parentKeys.includes(parentKey)) {
                  total += Number(balances[ck] || 0);
              }
          });
          return total;
      }

      // Fallback: sum all values in balances
      return Object.values(balances).reduce((sum: number, val: number) => {
          return sum + (Number(val) || 0);
      }, 0);
  };

  const calculatePointBalance = (provider: PointProvider) => {
      return transactions.reduce((acc, t) => {
          if (t.type === 'point' && t.pointProvider === provider) {
              return t.isPointUsage ? acc - t.amount : acc + t.amount;
          }
          if (t.type === 'expense' && t.method === PaymentMethod.POINTS && t.pointProvider === provider) {
              return acc - t.amount;
          }
          return acc;
      }, 0);
  };

  const handleEditClick = (methodKey: string, currentBalance: number) => {
    setEditingMethod(methodKey);
    setEditValue(currentBalance.toString());
  };

  const handleSaveBalance = async (methodKey: string, currentBalance: number) => {
    const targetBalance = normalizeNumber(editValue);
    
    const diff = targetBalance - currentBalance;
    if (diff === 0) {
      setEditingMethod(null);
      return;
    }

    setIsSaving(true);
    try {
      // Parse methodKey to get method and detail
      let method = methodKey as PaymentMethod;
      let paymentDetail: string | undefined = undefined;

      if (methodKey.includes(':')) {
          const parts = methodKey.split(':');
          method = parts[0] as PaymentMethod;
          paymentDetail = parts[1];
      }

      if (balanceSettings?.enabled) {
          // Update balance directly in settings
          const newBalances = { ...balanceSettings.balances, [methodKey]: targetBalance };
          const newSettings = {
              monthlyBudget,
              balanceSettings: {
                  ...balanceSettings,
                  balances: newBalances
              }
          };
          await storageService.updateSettings(userId, newSettings);
          
          // adjustment タイプで記録（storageService の auto-update は adjustment を無視するため二重計上にならない）
          const adjustmentTransaction: Omit<Transaction, 'id' | 'createdAt'> = {
            type: 'adjustment' as TransactionType,
            amount: targetBalance,
            place: '残高調整 (手動)',
            method: method,
            paymentDetail: paymentDetail,
            category: ExpenseCategory.OTHER,
            date: new Date().toISOString(),
            note: `残高修正: ${currentBalance}円 → ${targetBalance}円`
          };
          await storageService.addTransaction(userId, adjustmentTransaction);
      } else {
          // Legacy behavior: Add adjustment transaction
          const type: TransactionType = diff > 0 ? 'income' : 'expense';
          const category = diff > 0 ? IncomeCategory.OTHER : ExpenseCategory.OTHER;
          const amount = Math.abs(diff);
    
          const adjustmentTransaction: Omit<Transaction, 'id' | 'createdAt'> = {
            type,
            amount,
            place: '残高調整',
            method: method,
            paymentDetail: paymentDetail,
            category,
            date: new Date().toISOString(),
            note: `残高修正: ${currentBalance}円 → ${targetBalance}円`
          };
    
          await storageService.addTransaction(userId, adjustmentTransaction);
      }

      await loadData();
      setEditingMethod(null);
    } catch (e) {
      console.error(e);
      alert('残高の更新に失敗しました。');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBudget = async () => {
      const budget = normalizeNumber(budgetInput);
      if (budget < 0) return;
      
      setIsSavingBudget(true);
      try {
          await storageService.updateSettings(userId, { monthlyBudget: budget });
          setMonthlyBudget(budget);
          setIsEditingBudget(false);
      } catch (e) {
          console.error(e);
          alert('予算の保存に失敗しました。');
      } finally {
          setIsSavingBudget(false);
      }
  };

  // --- Dashboard Logic ---
  const expenses = transactions.filter(t => t.type === 'expense' || t.type === 'charge');
  const incomes = transactions.filter(t => t.type === 'income');

  const totalSpent = expenses.reduce((sum, e) => sum + e.amount, 0);
  const totalIncome = incomes.reduce((sum, i) => sum + i.amount, 0);
  
  // Use calculateTotalBalance for the displayed total balance
  const netBalance = calculateTotalBalance();

  // Sync balance to account DB if display is enabled (Background sync)
  useEffect(() => {
    if (!loading && showBalance && userId !== 'guest') {
        userService.updateCachedBalance(userId, netBalance);
    }
  }, [loading, showBalance, netBalance, userId]);

  // Monthly Calculations for Analysis
  const now = new Date();
  const currentMonthPrefix = now.toISOString().slice(0, 7); // YYYY-MM
  
  const monthlyExpenses = expenses.filter(t => t.date.startsWith(currentMonthPrefix));
  const totalMonthlySpent = monthlyExpenses.reduce((sum, e) => {
      if (e.type === 'charge' || e.category === ExpenseCategory.CHARGE) return sum;
      return sum + e.amount;
  }, 0);

  const budgetUsage = monthlyBudget > 0 ? (totalMonthlySpent / monthlyBudget) * 100 : 0;
  
  // Category Data (Monthly)
  const categoryAggregates = monthlyExpenses.reduce<Record<string, number>>((acc, curr) => {
    if (curr.type === 'charge') return acc;
    const currentAmount = acc[curr.category] || 0;
    acc[curr.category] = currentAmount + curr.amount;
    return acc;
  }, {});

  const categoryData = Object.entries(categoryAggregates)
    .map(([name, value]): { name: string; value: number } => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  // Method Data (Monthly)
  const methodAggregates = monthlyExpenses.reduce<Record<string, number>>((acc, curr) => {
    const methodKey = (curr.type === 'charge' && curr.sourceMethod) ? curr.sourceMethod : curr.method;
    const currentAmount = acc[methodKey] || 0;
    acc[methodKey] = currentAmount + curr.amount;
    return acc;
  }, {});

  const methodData = Object.entries(methodAggregates)
    .map(([method, value]): { name: string; value: number } => ({ 
      name: PAYMENT_METHOD_LABELS[method as keyof typeof PAYMENT_METHOD_LABELS] || method, 
      value 
    }))
    .sort((a, b) => b.value - a.value);

  // Daily Data (Last 7 Days)
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    return d.toISOString().split('T')[0];
  }).reverse();

  const dailyData = last7Days.map(date => {
    const total = expenses
        .filter(e => e.date.startsWith(date))
        .reduce((sum: number, e: Transaction) => sum + e.amount, 0);
    return {
        date: new Date(date).toLocaleDateString('ja-JP', { day: 'numeric', month: 'short' }),
        amount: total
    };
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-7 h-7 animate-spin" style={{color:'var(--forest)'}} />
      </div>
    );
  }

  const toggleVisibility = () => setShowBalance(prev => !prev);

  const CHART_COLORS = ['#2D5A3D','#C2673A','#C9A84C','#4A7C5F','#8B6914','#6B5E4A','#D4845C','#82a87a'];

  return (
    <div className="space-y-4">

      {/* ── Hero Balance Card ── */}
      <div
        className="relative rounded-2xl overflow-hidden cursor-pointer card-hover stagger-child animate-card"
        style={{background:'var(--forest)', minHeight:'160px'}}
        onClick={toggleVisibility}
      >
        {/* decorative ring - animated */}
        <div className="absolute top-[-40px] right-[-40px] w-48 h-48 rounded-full opacity-10 border-[32px] animate-spin-slow" style={{borderColor:'white'}} />
        <div className="absolute bottom-[-30px] left-[-20px] w-32 h-32 rounded-full opacity-5 border-[20px] animate-float-slow" style={{borderColor:'white'}} />
        <div className="absolute top-1/2 right-8 w-16 h-16 rounded-full opacity-5 animate-float" style={{background:'white', animationDelay:'1s'}} />

        <div className="relative p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase opacity-60 text-white mb-0.5 stagger-child animate-slide-right delay-100">Total Balance</p>
              <div className="relative h-10">
                <span className={`font-display text-4xl text-white font-600 transition-all duration-500 ${showBalance ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 blur-sm'} absolute`}
                  style={{transition:'all 0.4s cubic-bezier(0.22,1,0.36,1)'}}>
                  ¥<AnimatedNumber value={netBalance} />
                </span>
                <span className={`font-display text-4xl text-white font-600 tracking-widest transition-all duration-500 ${showBalance ? 'opacity-0 -translate-y-2 blur-sm' : 'opacity-40 translate-y-0'} absolute`}>
                  ¥ ****
                </span>
              </div>
            </div>
            <div className="opacity-40 hover:opacity-80 transition-all duration-200 hover:scale-110">
              {showBalance
                ? <EyeOff className="w-5 h-5 text-white transition-transform duration-300" />
                : <Eye className="w-5 h-5 text-white animate-pulse-soft" />}
            </div>
          </div>

          <div className="flex gap-6 mt-2 pt-3" style={{borderTop:'1px solid rgba(255,255,255,0.15)'}}>
            <div className="stagger-child animate-fade-up delay-200">
              <div className="flex items-center gap-1 mb-0.5">
                <ArrowDownLeft className="w-3 h-3 opacity-60 text-white" />
                <span className="text-[10px] uppercase tracking-wider opacity-60 text-white font-semibold">収入</span>
              </div>
              <span className={`font-mono text-sm font-500 text-white transition-all duration-400 ${showBalance ? 'opacity-90 translate-y-0' : 'opacity-30 translate-y-1'}`}>
                {showBalance ? `¥${totalIncome.toLocaleString()}` : '****'}
              </span>
            </div>
            <div className="stagger-child animate-fade-up delay-300">
              <div className="flex items-center gap-1 mb-0.5">
                <ArrowUpRight className="w-3 h-3 opacity-60 text-white" />
                <span className="text-[10px] uppercase tracking-wider opacity-60 text-white font-semibold">支出</span>
              </div>
              <span className={`font-mono text-sm font-500 text-white transition-all duration-400 ${showBalance ? 'opacity-90 translate-y-0' : 'opacity-30 translate-y-1'}`}>
                {showBalance ? `¥${totalSpent.toLocaleString()}` : '****'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tab Navigation ── */}
      <div className="flex gap-1 p-1 rounded-xl stagger-child animate-fade-up delay-150" style={{background:'var(--cream-dark)'}}>
        <button
          onClick={() => setActiveTab('assets')}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold transition-all duration-300"
          style={activeTab === 'assets'
            ? {background:'white', color:'var(--ink)', boxShadow:'0 1px 3px rgba(44,36,22,0.1)', transform:'scale(1.02)'}
            : {color:'var(--ink-muted)'}}
        >
          <Building2 className={`w-3.5 h-3.5 transition-transform duration-300 ${activeTab==='assets'?'scale-110':''}`} /> 資産・口座
        </button>
        <button
          onClick={() => setActiveTab('analysis')}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold transition-all duration-300"
          style={activeTab === 'analysis'
            ? {background:'white', color:'var(--ink)', boxShadow:'0 1px 3px rgba(44,36,22,0.1)', transform:'scale(1.02)'}
            : {color:'var(--ink-muted)'}}
        >
          <TrendingUp className={`w-3.5 h-3.5 transition-transform duration-300 ${activeTab==='analysis'?'scale-110':''}`} /> 分析
        </button>
      </div>

      {/* ── ASSETS TAB ── */}
      {activeTab === 'assets' && (
        <div className="space-y-3">
          {(() => {
            const allMethods = Array.from(new Set(balanceSettings?.enabled ? balanceSettings.includedMethods : DEFAULT_TARGET_METHODS));
            const parentMethods = allMethods.filter(m => !m.includes(':'));
            return parentMethods.map((parentKey: string, index: number) => {
              const currentBalance = calculateBalance(parentKey);
              const isEditing = editingMethod === parentKey;
              const childKeys = allMethods.filter(m => m.startsWith(`${parentKey}:`));
              const config = METHOD_CONFIG[parentKey] || { label: parentKey, icon: Wallet, color: 'from-slate-400 to-slate-500', text: 'text-slate-600', bg: 'bg-slate-50' };
              const Icon = config.icon;
              return (
                <div key={parentKey} className="card p-4 group stagger-child animate-fade-up card-hover" style={{animationDelay:`${0.2+index*0.1}s`}}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6" style={{background:'var(--forest-pale)'}}>
                        <Icon className="w-4.5 h-4.5" style={{color:'var(--forest)'}} size={18} />
                      </div>
                      <span className="font-semibold text-sm ink-underline" style={{color:'var(--ink)'}}>{config.label}</span>
                    </div>
                    {!isEditing && showBalance && (
                      <button
                        onClick={() => handleEditClick(parentKey, currentBalance)}
                        className="text-[10px] font-bold px-2.5 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-105 active:scale-95"
                        style={{background:'var(--cream-dark)', color:'var(--ink-muted)'}}
                      >
                        修正
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="rounded-xl p-3 animate-scale-in" style={{background:'var(--cream)'}}>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          className="input-warm flex-1 px-3 py-2 text-lg font-mono font-500"
                          autoFocus
                        />
                        <span className="text-sm font-semibold" style={{color:'var(--ink-muted)'}}>円</span>
                      </div>
                      <div className="flex justify-end gap-2 mt-2">
                        <button onClick={() => setEditingMethod(null)} className="p-2 rounded-lg" style={{background:'var(--cream-dark)', color:'var(--ink-muted)'}}>
                          <X className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleSaveBalance(parentKey, currentBalance)} disabled={isSaving}
                          className="btn-forest px-4 py-2 text-xs flex items-center gap-1.5">
                          {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} 更新
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="cursor-pointer" onClick={toggleVisibility}>
                      <span className={`font-display text-2xl font-600 transition-all duration-300 ${currentBalance < 0 ? 'amount-expense' : ''}`}
                        style={currentBalance >= 0 ? {color:'var(--ink)'} : {}}>
                        {showBalance ? `¥${currentBalance.toLocaleString()}` : '¥ ****'}
                      </span>
                    </div>
                  )}

                  {childKeys.length > 0 && (
                    <div className="mt-3 pt-3 space-y-2" style={{borderTop:'1px dashed rgba(44,36,22,0.1)'}}>
                      {childKeys.map(childKey => {
                        const childName = childKey.split(':')[1];
                        const childBalance = calculateBalance(childKey);
                        const isChildEditing = editingMethod === childKey;
                        return (
                          <div key={childKey} className="group/child">
                            {isChildEditing ? (
                              <div className="rounded-lg p-2.5 animate-scale-in" style={{background:'var(--cream)'}}>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-semibold w-20 shrink-0" style={{color:'var(--ink-light)'}}>{childName}</span>
                                  <input type="number" value={editValue} onChange={e => setEditValue(e.target.value)}
                                    className="input-warm flex-1 px-2 py-1.5 text-sm font-mono" autoFocus />
                                  <span className="text-xs" style={{color:'var(--ink-muted)'}}>円</span>
                                </div>
                                <div className="flex justify-end gap-2 mt-2">
                                  <button onClick={() => setEditingMethod(null)} className="p-1.5 rounded-lg" style={{background:'var(--cream-dark)', color:'var(--ink-muted)'}}>
                                    <X className="w-3 h-3" />
                                  </button>
                                  <button onClick={() => handleSaveBalance(childKey, childBalance)} disabled={isSaving}
                                    className="btn-forest px-3 py-1.5 text-[11px] flex items-center gap-1">
                                    <Save className="w-3 h-3" /> 保存
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between py-1">
                                <div className="flex items-center gap-2">
                                  <div className="w-1 h-1 rounded-full" style={{background:'var(--ink-muted)'}} />
                                  <span className="text-xs" style={{color:'var(--ink-light)'}}>{childName}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-xs font-500" style={{color: childBalance < 0 ? 'var(--terra)' : 'var(--ink)'}}>
                                    {showBalance ? `¥${childBalance.toLocaleString()}` : '¥ ****'}
                                  </span>
                                  {showBalance && (
                                    <button onClick={() => handleEditClick(childKey, childBalance)}
                                      className="text-[10px] px-2 py-0.5 rounded-md opacity-0 group-hover/child:opacity-100 transition-opacity"
                                      style={{color:'var(--forest)', background:'var(--forest-pale)'}}>
                                      修正
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            });
          })()}

          {/* Point Section */}
          {Object.values(PointProvider).some(p => calculatePointBalance(p) !== 0) && (
            <div className="card p-4 stagger-child animate-fade-up delay-400">
              <div className="flex items-center gap-2 mb-3">
                <Coins className="w-4 h-4 animate-swing" style={{color:'var(--gold)'}} />
                <h3 className="text-sm font-semibold" style={{color:'var(--ink)'}}>ポイント残高</h3>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {Object.values(PointProvider).map((provider, idx) => {
                  const balance = calculatePointBalance(provider);
                  if (balance === 0) return null;
                  return (
                    <div
                      key={provider}
                      className="rounded-xl p-3 cursor-pointer card-hover stagger-child animate-scale-bounce ripple-container"
                      style={{background:'var(--gold-pale)', animationDelay:`${idx*0.07}s`}}
                      onClick={toggleVisibility}
                    >
                      <div className="text-[10px] font-semibold mb-1" style={{color:'#8B6914'}}>{POINT_PROVIDER_LABELS[provider]}</div>
                      <span className="font-mono font-600 text-sm transition-all duration-300" style={{color: showBalance ? 'var(--ink)' : 'var(--ink-muted)'}}>
                        {showBalance ? `${balance.toLocaleString()} P` : '**** P'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ANALYSIS TAB ── */}
      {activeTab === 'analysis' && (
        <div className="space-y-4">

          {/* Budget Card */}
          <div className="card p-4 stagger-child animate-fade-up">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{background:'var(--terra-pale)'}}>
                  <Target className="w-4 h-4" style={{color:'var(--terra)'}} />
                </div>
                <h3 className="text-sm font-semibold" style={{color:'var(--ink)'}}>今月の予算</h3>
              </div>
              {!isEditingBudget && (
                <button onClick={() => { setBudgetInput(monthlyBudget.toString()); setIsEditingBudget(true); }}
                  className="p-1.5 rounded-lg" style={{background:'var(--cream-dark)', color:'var(--ink-muted)'}}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {isEditingBudget ? (
              <div className="flex gap-2 animate-scale-in">
                <input type="number" value={budgetInput} onChange={e => setBudgetInput(e.target.value)}
                  placeholder="例: 100000" className="input-warm flex-1 px-3 py-2 text-sm font-mono" autoFocus />
                <button onClick={() => setIsEditingBudget(false)} className="p-2 rounded-xl" style={{background:'var(--cream-dark)', color:'var(--ink-muted)'}}>
                  <X className="w-4 h-4" />
                </button>
                <button onClick={handleSaveBudget} disabled={isSavingBudget} className="btn-forest px-3 py-2 flex items-center">
                  {isSavingBudget ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </button>
              </div>
            ) : monthlyBudget > 0 ? (
              <div className="space-y-2">
                <div className="flex justify-between items-end">
                  <span className="font-display text-2xl font-600 animate-number stagger-child" style={{color:'var(--ink)'}}>
                    ¥<AnimatedNumber value={totalMonthlySpent} />
                  </span>
                  <span className="text-xs font-semibold animate-fade-in" style={{color:'var(--ink-muted)'}}>/ ¥{monthlyBudget.toLocaleString()}</span>
                </div>
                <div className="progress-bar-track">
                  <div
                    className="progress-bar-fill progress-animated"
                    style={{
                      width: `${Math.min(budgetUsage, 100)}%`,
                      background: budgetUsage > 100 ? 'var(--terra)' : budgetUsage > 80 ? 'var(--gold)' : 'var(--forest)',
                      transition: 'background 0.5s ease'
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs font-semibold">
                  <span className="animate-slide-right stagger-child" style={{color: budgetUsage > 100 ? 'var(--terra)' : 'var(--ink-muted)'}}>
                    {budgetUsage.toFixed(1)}% 消化
                    {budgetUsage > 100 && <span className="ml-1 animate-pulse-soft">⚠</span>}
                  </span>
                  <span className="animate-slide-left stagger-child" style={{color:'var(--forest)'}}>残り ¥{(monthlyBudget - totalMonthlySpent).toLocaleString()}</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-3 rounded-xl" style={{background:'var(--cream)'}}>
                <p className="text-xs mb-2" style={{color:'var(--ink-muted)'}}>予算が設定されていません</p>
                <button onClick={() => { setBudgetInput(''); setIsEditingBudget(true); }}
                  className="text-xs font-bold" style={{color:'var(--forest)'}}>予算を設定する</button>
              </div>
            )}
          </div>

          {/* 7-day bar chart */}
          <div className="card p-4 stagger-child animate-fade-up delay-100">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4" style={{color:'var(--forest)'}} />
              <h3 className="text-sm font-semibold" style={{color:'var(--ink)'}}>7日間の支出推移</h3>
            </div>
            <div className="h-44 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyData} margin={{top:0, right:0, left:-20, bottom:0}}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(44,36,22,0.06)" />
                  <XAxis dataKey="date" tick={{fontSize:10, fill:'#A89880'}} axisLine={false} tickLine={false} dy={8} />
                  <YAxis tick={{fontSize:10, fill:'#A89880'}} axisLine={false} tickLine={false} />
                  <Tooltip
                    cursor={{fill:'rgba(44,36,22,0.04)', radius:6}}
                    formatter={(value: number) => [`¥${value.toLocaleString()}`, '支出']}
                    contentStyle={{borderRadius:'12px', border:'1px solid rgba(44,36,22,0.08)', boxShadow:'0 4px 12px rgba(44,36,22,0.1)', fontSize:'12px', color:'var(--ink)'}}
                  />
                  <Bar dataKey="amount" fill="var(--forest)" radius={[5,5,5,5]} maxBarSize={28} animationDuration={1200} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Category pie chart */}
          {categoryData.length > 0 && (
            <div className="card p-4 stagger-child animate-fade-up delay-200">
              <h3 className="text-sm font-semibold mb-4" style={{color:'var(--ink)'}}>カテゴリ別支出（今月）</h3>
              <div className="h-52 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={categoryData} cx="50%" cy="50%" innerRadius={50} outerRadius={72}
                      paddingAngle={4} dataKey="value" cornerRadius={4} stroke="none" animationDuration={900}>
                      {categoryData.map((entry, index) => (
                        <Cell key={`c-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => `¥${v.toLocaleString()}`}
                      contentStyle={{borderRadius:'10px', border:'1px solid rgba(44,36,22,0.08)', fontSize:'12px'}} />
                    <Legend iconType="circle" layout="horizontal" verticalAlign="bottom" align="center"
                      wrapperStyle={{fontSize:'10px', paddingTop:'16px', color:'var(--ink-light)'}} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Payment method breakdown */}
          {methodData.length > 0 && (
            <div className="card p-4 stagger-child animate-fade-up delay-300">
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="w-4 h-4" style={{color:'var(--forest)'}} />
                <h3 className="text-sm font-semibold" style={{color:'var(--ink)'}}>支払方法別（今月）</h3>
              </div>
              <div className="space-y-2.5">
                {methodData.map((entry, index) => (
                  <div
                    key={entry.name}
                    className="flex items-center gap-3 stagger-child animate-slide-right"
                    style={{animationDelay:`${index * 0.07}s`}}
                  >
                    <div className="w-2.5 h-2.5 rounded-full shrink-0 transition-transform duration-200 hover:scale-150"
                      style={{background: CHART_COLORS[index % CHART_COLORS.length]}} />
                    <span className="text-xs flex-1 font-medium" style={{color:'var(--ink-light)'}}>{entry.name}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 rounded-full overflow-hidden" style={{background:'rgba(44,36,22,0.08)'}}>
                        <div
                          className="h-full rounded-full progress-animated"
                          style={{
                            background: CHART_COLORS[index % CHART_COLORS.length],
                            width: `${(entry.value / (methodData[0]?.value || 1)) * 100}%`,
                            animationDelay: `${index * 0.1}s`
                          }}
                        />
                      </div>
                      <span className="font-mono text-xs font-600 w-20 text-right tabular-nums" style={{color:'var(--ink)'}}>
                        ¥{entry.value.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
